import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Cell, Legend, type LegendProps, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { type DetailsWindowFieldsConfiguration } from '../../components/CustomDetailsWindow/utils';
import { SortByCustomSorting } from '../../data-transformer/column-line-chart/data-commands/sort-by-custom-sorting';
import { DeleteUndefinedAttributes } from '../../data-transformer/common-commands/delete-undefined-attributes';
import { FilterEmptyText } from '../../data-transformer/common-commands/filter-empty-text';
import { DataExecutor } from '../../data-transformer/data-executor';
import { BuildPieChartValuesByValues } from '../../data-transformer/pie-chart/build-pie-chart-values-by-values';
import { handleMouseEnter, handleMouseLeave } from '../../helpers/tooltip-functions';
import { usePieChartQuery } from '../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../redux/config/widgetApi';
import { getWidgets } from '../../redux/slices/layout-tabs-slice';
import type { PieChartValues } from '../../shared/widget-data.interfaces';
import OdinCustomDetailsWindow from '../CustomDetailsWindow/custom-details-window.component';
import CustomTooltip from '../CustomToolTip/CustomToolTip';
import ChartGenericWrapper from '../common/chart-generic-wrapper';
import PieChartLabel from '../common/custom-label.component';
import { renderLegend } from '../common/custom-legend.component';
import type { CustomColorProps } from '../WidgetSettingWindow/components/config-components.interfaces';
import type { LabelFormatValue } from '../WidgetSettingWindow/widget-settings-validation';
import type { ChartProps } from './chart.interface';
import type { LegendPayloadOnClick } from './chart-events';
import useInvalidateChartResponse from './hooks/useInvalidateChartResponse';
import useStartEndDates from './hooks/useStartEndDates';

const isValidLabelFormat = (value: unknown): value is LabelFormatValue => {
  return typeof value === 'string' && ['number', 'percentage', 'both'].includes(value);
};

export default function OdinPieChart({ widgetId, layoutProps }: ChartProps) {
  const [legendProps, setLegendProps] = useState<Record<string, boolean>>({});
  const [transformedData, setTransformedData] = useState<Array<PieChartValues>>();
  const [detailsWindowVisible, setDetailsWindowVisible] = useState<boolean>(false);
  const [modalData, setModalData] = useState<DetailsWindowFieldsConfiguration>();
  const [customColors, setCustomColors] = useState<CustomColorProps[]>();
  const { startDate, endDate } = useStartEndDates();

  const [configurationData, setConfigurationData] = useState<WidgetConfigurationData>();

  const widgets = useSelector(getWidgets);

  useEffect(() => {
    if (widgets) {
      setConfigurationData(widgets.find((widget) => +widget.widget.id === +widgetId)?.widget);
    }
  }, [widgets, widgetId]);

  const {
    data: widgetChartData,
    isLoading,
    isFetching: isWidgetChartDataFetching,
    isError,
    error,
  } = usePieChartQuery({
    widgetId,
    from: startDate,
    to: endDate,
  });

  useEffect(() => {
    if (widgetChartData && configurationData && !isWidgetChartDataFetching) {
      const commandExecutor = new DataExecutor();
      const dataOfPieChart = commandExecutor.execute(
        new BuildPieChartValuesByValues(),
        widgetChartData,
        configurationData
      );

      const colors: CustomColorProps[] = dataOfPieChart.map((element) => {
        return {
          label: element.field,
          color: element.color,
        };
      });
      setCustomColors(colors);

      const dataOfPieChartFiltered = commandExecutor.execute(
        new FilterEmptyText<PieChartValues>(),
        dataOfPieChart,
        configurationData
      );

      const removedUndefinedResponse = commandExecutor.execute(
        new DeleteUndefinedAttributes<PieChartValues>(),
        dataOfPieChartFiltered,
        configurationData
      );
      if (configurationData.widgetConfig?.sortOrder) {
        setTransformedData(
          commandExecutor.execute(new SortByCustomSorting(), removedUndefinedResponse, configurationData)
        );
      } else {
        setTransformedData(removedUndefinedResponse);
      }
    }
  }, [widgetChartData, configurationData, isWidgetChartDataFetching]);

  const legendSelection = (e: LegendPayloadOnClick) => {
    setLegendProps({
      ...legendProps,
      [(e.dataKey as string | number | undefined) || (e.value as string)]:
        !legendProps[(e.dataKey as string | number | undefined) || (e.value as string)],
    });
  };
  const customPayload = transformedData?.map((data: PieChartValues) => ({
    ...data,
    value: data.field,
    color: legendProps != null && legendProps[data.field as string] ? '#808080' : data.color,
    fill: data.color,
    type: 'rect',
  })) as LegendProps['payloadUniqBy'];

  const pieChartData = useMemo(() => {
    if (transformedData) {
      return transformedData.filter((item) => !(item.field && legendProps[item.field]));
    }
    return [];
  }, [legendProps, transformedData]);

  const onShowModal = (data: DetailsWindowFieldsConfiguration) => {
    console.log('modal data pie chart', data);
    setModalData(data);
    setDetailsWindowVisible(true);
  };

  const onDialogHidden = () => setDetailsWindowVisible(false);

  const handleRefetch = useInvalidateChartResponse(widgetId);

  return (
    <ChartGenericWrapper
      widgetId={widgetId}
      isLoading={isLoading}
      isFetching={isWidgetChartDataFetching}
      isError={isError}
      error={error}
      refetch={handleRefetch}
      widgetConfigurationData={configurationData && { ...configurationData, colors: customColors, layoutProps }}
      widgetChartData={widgetChartData}
      renderNoDataByDefault={!isWidgetChartDataFetching && !transformedData?.length}
    >
      {configurationData && (
        <>
          <OdinCustomDetailsWindow
            {...configurationData.widgetData}
            widgetId={widgetId}
            fieldValue={modalData?.firstField}
            secondFieldValue={modalData?.secondField}
            visible={detailsWindowVisible}
            onDialogHidden={onDialogHidden}
          />
          <ResponsiveContainer height="99%">
            <PieChart>
              <Legend onClick={(e) => legendSelection(e)} formatter={renderLegend} payloadUniqBy={customPayload} />
              {pieChartData.length > 0 ? (
                <>
                  <Tooltip content={<CustomTooltip type="pie" />} cursor={false} isAnimationActive={false} />
                  <Pie
                    name="Quantity"
                    paddingAngle={0}
                    minAngle={1}
                    data={pieChartData}
                    dataKey="value"
                    nameKey="field"
                    label={(props) => {
                      const labelFormat = configurationData?.widgetConfig?.labelFormat;
                      const validLabelFormat = isValidLabelFormat(labelFormat) ? labelFormat : 'percentage';

                      return (
                        <PieChartLabel
                          {...props}
                          labelFormat={validLabelFormat}
                          labelDecimals={configurationData?.widgetConfig?.labelDecimals ?? 0}
                        />
                      );
                    }}
                    labelLine={false}
                    outerRadius="90%"
                    onClick={(data) => {
                      const firstField = data.field;
                      const secondField = data.dataKey;
                      onShowModal({ firstField, secondField });
                    }}
                    onMouseEnter={() => handleMouseEnter('Quantity')}
                    onMouseLeave={handleMouseLeave}
                    isAnimationActive={false}
                    cursor="pointer"
                  >
                    {pieChartData.map((entry, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} style={{ outline: 'none' }} />
                    ))}
                  </Pie>
                </>
              ) : (
                // Show empty Pie chart
                <Pie
                  dataKey="value"
                  outerRadius="90%"
                  nameKey="field"
                  data={[{ field: '', value: 1 }]}
                  fill="#eeeeee"
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        </>
      )}
    </ChartGenericWrapper>
  );
}
